import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Store, Users, ShieldAlert, UserRoundCheck, Plus, RefreshCw, Loader2 } from "lucide-react";
import logo from "@/assets/logo.png";

import { authClient } from "@/lib/auth-client";
import { orpc } from "@/utils/orpc";
import { Button } from "@pos-vidyon/ui/components/button";
import { Input } from "@pos-vidyon/ui/components/input";
import { Label } from "@pos-vidyon/ui/components/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@pos-vidyon/ui/components/card";

export const Route = createFileRoute("/superadmin")({
  beforeLoad: async ({ context }) => {
    const session = context.session;
    if (!session || !session.data) {
      throw redirect({ to: "/login" });
    }
    if ((session.data.user as { role?: string }).role !== "admin") {
      throw redirect({ to: "/dashboard" });
    }
    return { session: session.data };
  },
  loader: async ({ context: { queryClient, orpc } }) => {
    await Promise.all([
      queryClient.ensureQueryData(orpc.admin.getOrganizations.queryOptions()),
      queryClient.ensureQueryData(orpc.admin.getUsers.queryOptions()),
    ]);
  },
  component: SuperadminDashboard,
});

function SuperadminDashboard() {
  const { session } = Route.useRouteContext();
  const router = useRouter();
  const queryClient = useQueryClient();
  const [newOrgName, setNewOrgName] = useState("");
  const [newOrgLogo, setNewOrgLogo] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");

  // Modal States for adding cafe to existing user
  const [addCafeUser, setAddCafeUser] = useState<(typeof users)[number] | null>(null);
  const [modalCafeName, setModalCafeName] = useState("");
  const [modalCafeLogo, setModalCafeLogo] = useState("");

  // Mutations
  const createOrgMutation = useMutation({
    ...orpc.admin.createOrganizationWithOwner.mutationOptions(),
    onSuccess: () => {
      toast.success("Cafe and new owner account created successfully!");
      setNewOrgName("");
      setNewOrgLogo("");
      setOwnerName("");
      setOwnerEmail("");
      setOwnerPassword("");
      void refetchOrgs();
      void refetchUsers();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to create cafe");
    },
  });

  const createOrgForExistingMutation = useMutation({
    ...orpc.admin.createOrganizationForExistingOwner.mutationOptions(),
    onSuccess: () => {
      toast.success("Cafe created and linked successfully!");
      setModalCafeName("");
      setModalCafeLogo("");
      setAddCafeUser(null);
      void refetchOrgs();
      void refetchUsers();
    },
    onError: (err) => {
      toast.error(err.message || "Failed to create cafe");
    },
  });

  // Queries
  const {
    data: orgs = [],
    isLoading: isLoadingOrgs,
    refetch: refetchOrgs,
    isRefetching: isRefetchingOrgs,
  } = useQuery(orpc.admin.getOrganizations.queryOptions());

  const {
    data: users = [],
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
    isRefetching: isRefetchingUsers,
  } = useQuery(orpc.admin.getUsers.queryOptions());

  // Handlers
  const handleCreateOrg = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newOrgName.trim()) {
      toast.error("Please enter a cafe name");
      return;
    }
    if (!ownerName.trim()) {
      toast.error("Please enter owner name");
      return;
    }
    if (!ownerEmail.trim()) {
      toast.error("Please enter owner email");
      return;
    }
    if (ownerPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    createOrgMutation.mutate({
      name: newOrgName,
      logo: newOrgLogo || undefined,
      ownerName,
      ownerEmail,
      ownerPassword,
    });
  };

  const handleModalCreateOrg = (e: React.FormEvent) => {
    e.preventDefault();
    if (!addCafeUser) return;
    if (!modalCafeName.trim()) {
      toast.error("Please enter a cafe name");
      return;
    }

    createOrgForExistingMutation.mutate({
      name: modalCafeName,
      logo: modalCafeLogo || undefined,
      ownerId: addCafeUser.id,
    });
  };

  const handleImpersonate = async (targetUserId: string) => {
    toast.promise(
      authClient.admin.impersonateUser({ userId: targetUserId }).then(async () => {
        // Impersonation creates a brand-new session without an active organization.
        // Pre-select the impersonated user's first organization so tenant-scoped
        // pages (staff, menu, etc.) do not fail with "No active organization".
        const orgsRes = await authClient.organization.list();
        if (orgsRes.data?.length) {
          await authClient.organization.setActive({
            organizationId: orgsRes.data[0].id,
          });
        }

        // Clear TanStack Query cache and navigate to dashboard natively client-side
        queryClient.clear();
        await router.navigate({ to: "/dashboard" });
      }),
      {
        loading: "Switching session...",
        success: "Session switched! Redirecting to dashboard...",
        error: (err) => err.message || "Failed to impersonate user",
      },
    );
  };

  const handleLogout = async () => {
    await authClient.signOut();
    queryClient.clear();
    await router.navigate({ to: "/login" });
  };

  const isPageLoading = isLoadingOrgs || isLoadingUsers;

  return (
    <div className="min-h-screen bg-slate-50 text-gray-800 flex flex-col font-sans">
      {/* Admin Navbar */}
      <header className="border-b border-gray-100 bg-white/80 backdrop-blur-md px-6 py-4 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl  flex items-center justify-center">
            <img src={logo} alt="Vidyon Logo" className="h-6 w-auto object-contain" />
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right leading-tight">
            <p className="text-xs font-bold text-gray-900">{session.user.name}</p>
            <p className="text-[10px] text-rose-500 font-bold uppercase tracking-wider">System Admin</p>
          </div>
          <button
            onClick={handleLogout}
            className="rounded-xl border border-gray-200 bg-white hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 py-1.5 px-3.5 text-xs font-bold text-gray-500 transition-all shadow-sm cursor-pointer"
          >
            Çıkış Yap
          </button>
        </div>
      </header>

      {/* Main Layout */}
      <main className="flex-1 p-6 md:p-8 max-w-7xl w-full mx-auto space-y-6">
        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Active Cafes */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Aktif Kafeler (Organizasyonlar)</p>
              <p className="text-3xl font-black tracking-tight text-gray-900">{orgs.length}</p>
            </div>
            <div className="p-3 bg-indigo-50 rounded-2xl border border-indigo-100 text-indigo-600">
              <Store className="h-6 w-6" />
            </div>
          </div>

          {/* Registered Users */}
          <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Kayıtlı Kullanıcılar</p>
              <p className="text-3xl font-black tracking-tight text-gray-900">{users.length}</p>
            </div>
            <div className="p-3 bg-emerald-50 rounded-2xl border border-emerald-100 text-emerald-600">
              <Users className="h-6 w-6" />
            </div>
          </div>
        </div>

        {isPageLoading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Sistem Veritabanı Yükleniyor...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left/Middle Column - Users list & Cafes list */}
            <div className="lg:col-span-2 space-y-6">
              {/* Users list */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-100 p-5">
                  <div>
                    <h3 className="text-sm font-black text-gray-900">Sistem Kullanıcıları</h3>
                    <p className="text-xs text-gray-400 font-medium">Kullanıcıların oturumuna geçiş (impersonate) yapın veya cafe atayın.</p>
                  </div>
                  <button
                    onClick={() => refetchUsers()}
                    className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-all shadow-sm cursor-pointer"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${isRefetchingUsers ? "animate-spin" : ""}`} />
                  </button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead className="bg-gray-50/75 text-gray-400 font-bold border-b border-gray-100 uppercase tracking-wider">
                      <tr>
                        <th className="px-5 py-3">İsim</th>
                        <th className="px-5 py-3">E-posta</th>
                        <th className="px-5 py-3">Rol</th>
                        <th className="px-5 py-3">Kafeler</th>
                        <th className="px-5 py-3 text-right">İşlemler</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {users.map((u) => (
                        <tr key={u.id} className="hover:bg-gray-50/40 transition-colors">
                          <td className="px-5 py-4 font-bold text-gray-900">{u.name}</td>
                          <td className="px-5 py-4 font-semibold text-gray-500">{u.email}</td>
                          <td className="px-5 py-4">
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${u.role === "admin"
                                ? "bg-rose-50 text-rose-600 border-rose-100"
                                : "bg-gray-50 text-gray-500 border-gray-200"
                                }`}
                            >
                              {u.role}
                            </span>
                          </td>
                          <td className="px-5 py-4">
                            {u.role === "admin" ? (
                              <span className="text-[10px] text-gray-400 italic">
                                N/A (Süperadmin)
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1.5 items-center">
                                {u.organizations?.map((org) => (
                                  <span
                                    key={org.id}
                                    className="inline-flex items-center gap-1 bg-indigo-50 text-indigo-600 border border-indigo-100 px-2 py-0.5 rounded-lg text-[10px] font-bold"
                                  >
                                    {org.logo && (
                                      <img
                                        src={org.logo}
                                        alt=""
                                        className="size-3.5 rounded object-cover"
                                      />
                                    )}
                                    {org.name}
                                  </span>
                                ))}
                                <button
                                  onClick={() => {
                                    setAddCafeUser(u);
                                    setModalCafeName("");
                                    setModalCafeLogo("");
                                  }}
                                  className="h-6 px-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700 text-[10px] font-extrabold flex items-center gap-1 border border-dashed border-rose-200 rounded-lg transition-all cursor-pointer"
                                >
                                  <Plus className="size-3" />
                                  Kafe Ekle
                                </button>
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            {u.id !== session.user.id && (
                              <button
                                onClick={() => handleImpersonate(u.id)}
                                className="bg-gray-900 hover:bg-gray-800 text-white font-extrabold text-[10px] uppercase tracking-wider flex items-center gap-1.5 py-1.5 px-3 rounded-lg shadow-sm transition-all ml-auto cursor-pointer"
                              >
                                <UserRoundCheck className="h-3.5 w-3.5" />
                                Oturuma Geç
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Cafes list */}
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between border-b border-gray-100 p-5">
                  <div>
                    <h3 className="text-sm font-black text-gray-900">Kurulu Kafeler</h3>
                    <p className="text-xs text-gray-400 font-medium">Sahibine atanmış çoklu şubeli organizasyonlar.</p>
                  </div>
                  <button
                    onClick={() => refetchOrgs()}
                    className="p-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-400 hover:text-gray-700 transition-all shadow-sm cursor-pointer"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${isRefetchingOrgs ? "animate-spin" : ""}`} />
                  </button>
                </div>
                <div>
                  {orgs.length === 0 ? (
                    <div className="p-8 text-center text-gray-400 font-semibold">
                      Henüz hiçbir kafe oluşturulmamış. Yan panelden yeni bir şube oluşturun.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-gray-50/75 text-gray-400 font-bold border-b border-gray-100 uppercase tracking-wider">
                          <tr>
                            <th className="px-5 py-3">Kafe Adı</th>
                            <th className="px-5 py-3">Slug</th>
                            <th className="px-5 py-3">Sahibi</th>
                            <th className="px-5 py-3 text-center">Üye Sayısı</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {orgs.map((org) => (
                            <tr key={org.id} className="hover:bg-gray-50/40 transition-colors">
                              <td className="px-5 py-4 font-bold text-gray-900 flex items-center gap-2">
                                {org.logo && (
                                  <img src={org.logo} alt="" className="size-5 rounded-lg object-cover border border-gray-100 shadow-sm" />
                                )}
                                {org.name}
                              </td>
                              <td className="px-5 py-4 text-indigo-600 font-bold text-[10px] uppercase tracking-wider">{org.slug}</td>
                              <td className="px-5 py-4">
                                {org.owner ? (
                                  <div>
                                    <div className="font-bold text-gray-800">
                                      {org.owner.name}
                                    </div>
                                    <div className="text-[10px] text-gray-400 font-semibold">{org.owner.email}</div>
                                  </div>
                                ) : (
                                  <span className="text-rose-500 font-bold text-[10px] uppercase tracking-wider">Sahip Atanmamış</span>
                                )}
                              </td>
                              <td className="px-5 py-4 text-center">
                                <span className="inline-flex items-center justify-center bg-gray-50 text-gray-600 border border-gray-200 rounded-lg px-2.5 py-1 text-[10px] font-bold">
                                  {org.membersCount} Üye
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Provision Cafe Form */}
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-black text-gray-900">Yeni Kafe Tanımla</h3>
                  <p className="text-xs text-gray-400 font-medium">Sisteme yeni bir organizasyon şubesi ve yöneticisi tanımlayın.</p>
                </div>
                <form onSubmit={handleCreateOrg} className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Kafe Marka Adı
                    </label>
                    <input
                      placeholder="Örn: Nero Coffee Shop"
                      value={newOrgName}
                      onChange={(e) => setNewOrgName(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                      Kafe Logo URL (Opsiyonel)
                    </label>
                    <input
                      placeholder="Örn: https://logo.com/my-logo.png"
                      value={newOrgLogo}
                      onChange={(e) => setNewOrgLogo(e.target.value)}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                    />
                  </div>

                  <div className="border-t border-gray-100 my-4 pt-4">
                    <h4 className="text-[10px] font-black text-rose-600 uppercase tracking-wider mb-3">
                      Yönetici Hesap Bilgileri
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Ad Soyad
                        </label>
                        <input
                          placeholder="Örn: John Doe"
                          value={ownerName}
                          onChange={(e) => setOwnerName(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          E-posta Adresi
                        </label>
                        <input
                          type="email"
                          placeholder="Örn: john@cafe.com"
                          value={ownerEmail}
                          onChange={(e) => setOwnerEmail(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                          Parola
                        </label>
                        <input
                          type="password"
                          placeholder="En az 8 karakter"
                          value={ownerPassword}
                          onChange={(e) => setOwnerPassword(e.target.value)}
                          className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={createOrgMutation.isPending}
                    className="w-full bg-gray-900 hover:bg-gray-800 text-white font-extrabold text-xs uppercase tracking-wider flex items-center justify-center gap-2 py-2.5 rounded-xl shadow-sm transition-all mt-2 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {createOrgMutation.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Plus className="h-4 w-4" />
                    )}
                    Kafe ve Sahibi Oluştur
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Add Cafe Modal Overlay */}
      {addCafeUser && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white border border-gray-200 rounded-2xl p-6 w-full max-w-md shadow-2xl relative space-y-4 animate-slide-in-right text-gray-800">
            <div>
              <h3 className="text-sm font-black text-gray-900">{addCafeUser.name} İçin Kafe Ekle</h3>
              <p className="text-xs text-gray-400 font-medium">
                Yeni bir şube tanımlayıp doğrudan {addCafeUser.email} kullanıcısına bağlayın.
              </p>
            </div>

            <form onSubmit={handleModalCreateOrg} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Kafe / Marka Adı
                </label>
                <input
                  placeholder="Örn: Nero Coffee Shop"
                  value={modalCafeName}
                  onChange={(e) => setModalCafeName(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                  Kafe Logo URL (Opsiyonel)
                </label>
                <input
                  placeholder="Örn: https://logo.com/logo.png"
                  value={modalCafeLogo}
                  onChange={(e) => setModalCafeLogo(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-gray-900 placeholder:text-gray-400 text-gray-900"
                />
              </div>

              <div className="flex items-center justify-end gap-2.5 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setAddCafeUser(null)}
                  className="rounded-xl border border-gray-200 bg-white py-2 px-4 text-xs font-bold text-gray-500 hover:bg-gray-50 transition-all cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  disabled={createOrgForExistingMutation.isPending}
                  className="bg-gray-900 hover:bg-gray-800 text-white font-extrabold text-xs uppercase tracking-wider flex items-center gap-2 py-2 px-4 rounded-xl shadow-sm transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {createOrgForExistingMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Kafe Şubesi Ekle
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
